/**
 * REST members endpoints — CLAUDE.md §3, docs/CONTRACTS.md §REST-API.
 *
 *   POST   /members                  — add a member by mobile number
 *   GET    /members                  — list members the caller can manage
 *   DELETE /members/{id}             — remove a member
 *   POST   /members/{id}/devices     — assign devices to a member (additive)
 *   POST   /members/{id}/floors      — assign floor access to a member (additive)
 *
 * Caller must be strictly above the target authority_role in the §3 hierarchy
 * (or hold the explicit members.add / members.remove permission grant).
 */
import { z } from 'zod';
import { UuidSchema } from '../common/id.js';
import { E164MobileSchema } from '../common/mobile.js';
import { Iso8601Schema } from '../common/timestamp.js';
// ── Shared member schema ───────────────────────────────────────────────────
export const MemberAuthorityRoleSchema = z.enum([
    'main_user',
    'sub_main_user',
    'member',
    'sub_member',
]);
export const MemberStatusSchema = z.enum(['active', 'blocked']);
export const MemberSchema = z.object({
    id: UuidSchema,
    name: z.string().min(1).max(128),
    mobile: E164MobileSchema,
    authorityRole: MemberAuthorityRoleSchema,
    /** 'blocked' revokes the member's access (their linked user account is blocked too). */
    status: MemberStatusSchema,
    parentUserId: UuidSchema,
    /** Set once the member logs in for the first time via OTP. */
    userId: UuidSchema.nullable(),
    createdAt: Iso8601Schema,
});
/** Lean per-item shape for the list endpoint — omits server-internal fields
 *  (parentUserId, userId, createdAt) that no client screen displays.
 *
 *  GET /members returns the caller's whole SUBTREE, not just direct children:
 *  a sub_main_user who added two members sees any sub_member those members
 *  added below them, and so on down the hierarchy. `direct` + the addedBy*
 *  fields let the app label indirect rows ("added by <name>"). */
export const MemberListItemSchema = MemberSchema.omit({
    parentUserId: true,
    userId: true,
    createdAt: true,
}).extend({
    /** True when the caller added this member themselves (depth 0). */
    direct: z.boolean().optional(),
    /** Display name of whoever added this member, or null when unset. */
    addedByName: z.string().nullable().optional(),
    /** Mobile of whoever added this member. Null only on legacy rows. */
    addedByMobile: z.string().nullable().optional(),
});
// ── POST /members ──────────────────────────────────────────────────────────
export const CreateMemberBody = z.object({
    name: z.string().min(1).max(128),
    mobile: E164MobileSchema,
    authorityRole: MemberAuthorityRoleSchema,
});
export const CreateMemberResponse = MemberListItemSchema;
// ── PATCH /members/{id} — edit name/role and/or block/unblock ──────────────
export const UpdateMemberBody = z
    .object({
    name: z.string().min(1).max(128).optional(),
    authorityRole: MemberAuthorityRoleSchema.optional(),
    status: MemberStatusSchema.optional(),
})
    .refine((b) => b.name !== undefined || b.authorityRole !== undefined || b.status !== undefined, {
    message: 'At least one of name, authorityRole, status is required.',
});
export const UpdateMemberResponse = MemberListItemSchema;
export const ListMembersResponse = z.object({
    members: z.array(MemberListItemSchema),
});
// ── DELETE /members/{id} ───────────────────────────────────────────────────
// No body; 204 No Content on success.
// ── POST /members/{id}/devices ────────────────────────────────────────────
export const AssignDevicesBody = z.object({
    /** UUID list of devices to assign. Idempotent — re-assigning is a no-op. */
    deviceIds: z.array(UuidSchema).min(1),
});
export const AssignDevicesResponse = z.object({
    assigned: z.array(UuidSchema),
});
// ── POST /members/{id}/floors ─────────────────────────────────────────────
export const FloorAccessEntrySchema = z.object({
    floorId: UuidSchema,
    canCall: z.boolean().default(true),
    canConfigure: z.boolean().default(false),
});
export const AssignFloorsBody = z.object({
    /** Floor access entries to add. Idempotent — re-assigning is a no-op. */
    floors: z.array(FloorAccessEntrySchema).min(1),
});
export const AssignFloorsResponse = z.object({
    assigned: z.array(UuidSchema),
});
//# sourceMappingURL=members.js.map